import React, { useRef, useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  VStack,
  HStack,
  Box,
  Text,
  Button,
  Textarea,
  FormControl,
  FormLabel,
  useToast,
  Alert,
  AlertIcon,
  Image,
  Checkbox,
} from '@chakra-ui/react';
import SignatureCanvas from 'react-signature-canvas';
import { supabase } from '../../supabaseClient';

export default function PhysicianSignatureModal({
  isOpen,
  onClose,
  submission,
  doctorSession,
  onApproved,
}) {
  const sigCanvas = useRef(null);
  const [notes, setNotes] = useState('');
  const [signing, setSigning] = useState(false);
  const [useSaved, setUseSaved] = useState(false);
  const [savedSignatureUrl, setSavedSignatureUrl] = useState(null);
  const [saveForFuture, setSaveForFuture] = useState(false);
  const toast = useToast();

  // Load saved signature on open
  useEffect(() => {
    if (isOpen && doctorSession?.id) {
      // Check localStorage for saved signature
      const saved = localStorage.getItem(`doctor_signature_${doctorSession.id}`);
      if (saved) {
        setSavedSignatureUrl(saved);
        setUseSaved(true);
      } else if (doctorSession.agreement_signature_url) {
        // Fall back to agreement signature
        setSavedSignatureUrl(doctorSession.agreement_signature_url);
        setUseSaved(true);
      } else {
        setSavedSignatureUrl(null);
        setUseSaved(false);
      }
    }
  }, [isOpen, doctorSession?.id, doctorSession?.agreement_signature_url]);

  const handleClear = () => sigCanvas.current?.clear();

  const handleClose = () => {
    setNotes('');
    handleClear();
    setUseSaved(!!savedSignatureUrl);
    setSaveForFuture(false);
    onClose();
  };

  const handleSign = async () => {
    let signatureUrl;

    if (useSaved && savedSignatureUrl) {
      // Use the saved signature directly
      signatureUrl = savedSignatureUrl;
    } else {
      // Draw new signature
      if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
        toast({ title: 'Please provide your signature', status: 'warning', duration: 3000 });
        return;
      }

      setSigning(true);
      try {
        // Convert canvas to blob
        const dataURL = sigCanvas.current.getCanvas().toDataURL('image/png');
        const response = await fetch(dataURL);
        const blob = await response.blob();

        // Upload to doctor-signatures bucket
        const fileName = `physician-approval-${submission.id}-${doctorSession.id}-${Date.now()}.png`;
        const { error: uploadError } = await supabase.storage
          .from('doctor-signatures')
          .upload(fileName, blob, { contentType: 'image/png', upsert: true });
        if (uploadError) throw uploadError;

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('doctor-signatures')
          .getPublicUrl(fileName);

        signatureUrl = urlData.publicUrl;

        // Save for future use if checked
        if (saveForFuture) {
          localStorage.setItem(`doctor_signature_${doctorSession.id}`, signatureUrl);
          setSavedSignatureUrl(signatureUrl);
        }
      } catch (err) {
        toast({ title: 'Failed to upload signature', description: err.message, status: 'error', duration: 5000 });
        setSigning(false);
        return;
      }
    }

    setSigning(true);
    try {
      // Update submission
      const now = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('submissions')
        .update({
          physician_approved: true,
          physician_approved_at: now,
          physician_signature_url: signatureUrl,
          physician_approval_notes: notes.trim() || null,
          physician_approved_by: doctorSession.id,
        })
        .eq('id', submission.id);
      if (updateError) throw updateError;

      const updated = {
        ...submission,
        physician_approved: true,
        physician_approved_at: now,
        physician_signature_url: signatureUrl,
        physician_approval_notes: notes.trim() || null,
        physician_approved_by: doctorSession.id,
      };
      onApproved(updated);

      toast({ title: 'Results approved and signed', status: 'success', duration: 3000 });
      handleClose();
    } catch (err) {
      toast({
        title: 'Failed to sign',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSigning(false);
    }
  };

  const handleRemoveSaved = () => {
    localStorage.removeItem(`doctor_signature_${doctorSession.id}`);
    setSavedSignatureUrl(null);
    setUseSaved(false);
  };

  const accession = submission?.draw_code || (submission?.id ? String(submission.id).slice(-8) : '');

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" closeOnOverlayClick={false}>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent borderRadius="xl">
        <ModalHeader bg="blue.50" borderBottomWidth="1px" borderColor="gray.200">
          Approve & Sign Results
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={6}>
          <VStack spacing={5} align="stretch">
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">{submission?.patient_name}</Text>
                <Text fontSize="sm">Accession: {accession}</Text>
              </Box>
            </Alert>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Approval Notes (optional)
              </FormLabel>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this result approval..."
                size="sm"
                rows={2}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Physician Signature
              </FormLabel>

              {savedSignatureUrl && (
                <Box mb={3} p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="green.700">Saved Signature</Text>
                    <HStack spacing={2}>
                      <Button size="xs" variant="ghost" colorScheme="red" onClick={handleRemoveSaved}>
                        Remove
                      </Button>
                    </HStack>
                  </HStack>
                  <Box
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={2}
                    cursor="pointer"
                    onClick={() => setUseSaved(true)}
                    outline={useSaved ? '3px solid' : 'none'}
                    outlineColor={useSaved ? 'blue.400' : 'transparent'}
                  >
                    <Image src={savedSignatureUrl} alt="Saved Signature" maxH="80px" mx="auto" />
                  </Box>
                  {useSaved && (
                    <Text fontSize="xs" color="blue.600" mt={1} textAlign="center" fontWeight="medium">
                      Using saved signature
                    </Text>
                  )}
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="blue"
                    mt={2}
                    onClick={() => setUseSaved(false)}
                  >
                    Draw a new signature instead
                  </Button>
                </Box>
              )}

              {!useSaved && (
                <>
                  <Box
                    border="2px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                    bg="white"
                    p={1}
                  >
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        width: 460,
                        height: 150,
                        style: { width: '100%', height: '150px' },
                      }}
                      backgroundColor="white"
                    />
                  </Box>
                  <HStack mt={2} justify="space-between">
                    <HStack>
                      <Button size="xs" variant="outline" onClick={handleClear}>
                        Clear
                      </Button>
                      <Text fontSize="xs" color="gray.500">
                        Sign above using mouse or touchscreen
                      </Text>
                    </HStack>
                  </HStack>
                  <Checkbox
                    mt={2}
                    size="sm"
                    isChecked={saveForFuture}
                    onChange={(e) => setSaveForFuture(e.target.checked)}
                    colorScheme="green"
                  >
                    <Text fontSize="xs" color="gray.600">Save this signature for future approvals</Text>
                  </Checkbox>
                  {savedSignatureUrl && (
                    <Button
                      size="xs"
                      variant="link"
                      colorScheme="blue"
                      mt={1}
                      onClick={() => setUseSaved(true)}
                    >
                      Use saved signature instead
                    </Button>
                  )}
                </>
              )}
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderColor="gray.200" bg="gray.50">
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSign}
            isLoading={signing}
            loadingText="Signing..."
          >
            Approve & Sign
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
